import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  Collapse, IconButton, ListItemIcon, MenuList,
} from '@material-ui/core';
import {
  Add, DeleteOutline, EditOutlined, ExpandLess, ExpandMore, GroupOutlined,
} from '@material-ui/icons';
import {
  compose, get, groupBy, includes, map, pickBy, reduce, reject, sumBy, toNumber, toString,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '../../../common/components';
import { readableHiddenContent } from '../../../common/styles';
import messages from '../../messages';
import DeleteGroupCheckDialog from '../DeleteGroupCheckDialog';

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      width: 480px;
      padding: 32px 40px 24px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: 24px;
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  DialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  GroupList: styled(MenuList)`
    height: 354px;
    margin-bottom: 8px;
    overflow: auto;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    outline: none;
  `,
  GroupItem: styled.li`
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 40px;
    margin: 0;
    padding: 4px ${({ padding = {} }) => padding.right || 24}px 4px 28px;
    font-size: 1rem;
    text-align: left;
    text-decoration: none;
    vertical-align: middle;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    cursor: pointer;
    outline: none;
    user-select: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[25], 0.5)};
    }
    :hover .MuiIconButton-root {
      opacity: 1;
    }
  `,
  ExpandMore: styled(ExpandMore)`
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 1.25rem;
  `,
  ExpandLess: styled(ExpandLess)`
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 1.25rem;
  `,
  SubgroupList: styled(MenuList)`
    padding: 0;
    outline: none;
  `,
  SubgroupItem: styled.li`
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 40px;
    margin: 0;
    padding: 8px 21px 9px 64px;
    font-size: 1rem;
    text-align: left;
    text-decoration: none;
    vertical-align: middle;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    cursor: pointer;
    outline: none;
    user-select: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[25], 0.5)};
    }
    :hover .MuiIconButton-root {
      opacity: 1;
    }
  `,
  ListItemIcon: styled(ListItemIcon)`
    min-width: auto;
    margin-right: 8px;
  `,
  ListItemText: styled.p`
    margin-right: auto;
    margin-left: 8px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
  `,
  Label: styled.label`
    ${readableHiddenContent};
  `,
  Input: styled.input`
    width: 224px;
    height: 32px;
    margin-right: auto;
    padding-left: 8px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    outline: none;
    :focus,
    :hover {
      border: 2px solid ${({ theme }) => theme.palette.primary.main};
    }
  `,
  FlatButton: styled.button`
    position: relative;
    left: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 48px;
    height: 32px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    line-height: 1.54;
    cursor: pointer;
    :focus,
    :hover {
      text-decoration: underline;
    }
  `,
  IconButton: styled(IconButton)`
    opacity: 0;
    :not(:last-of-type) {
      margin-right: 8px;
    }
    :focus,
    :hover {
      opacity: 1;
    }
  `,
  EditOutlined: styled(EditOutlined)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  DeleteOutline: styled(DeleteOutline)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  GroupOutlined: styled(GroupOutlined)`
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  GroupAddButton: styled(Button)`
    min-height: 40px;
    margin-left: 20px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
    line-height: 1.53;
  `,
  Add: styled(Add)`
    margin-right: 12px;
    color: ${({ theme }) => theme.palette.grey[700]};
    font-size: 1.25rem;
  `,
  DialogActions: styled(DialogActions)`
    display: flex;
  `,
};

const GroupSettingsDialog = ({
  groups, open, deleteGroupCheckOpen, onUpdate, onClose, onGroupAddOpen, onDeleteGroupCheckOpen, onDeleteGroupCheckClose, match,
}) => {
  const [collapsed, setCollapsed] = useState(null);
  const [editing, setEditing] = useState({ value: null, type: null });
  const [prevValue, setPrevValue] = useState('');
  const [group, setGroup] = useState({ value: null, type: null, id: null });

  const openEditView = useCallback((event) => {
    const { dataset } = event.currentTarget;

    setEditing({ value: toNumber(dataset.value), type: dataset.type });

    setPrevValue(dataset.name);
  }, []);
  const closeEditView = useCallback(() => setEditing({ value: null, type: null }), []);
  const handleClick = useCallback(() => onGroupAddOpen(), [onGroupAddOpen]);
  const collapse = useCallback((event) => {
    const { dataset } = event.currentTarget;

    setCollapsed(prevCollapsed => (prevCollapsed === dataset.value ? null : dataset.value));
  }, []);
  const handleSubmit = useCallback(values => onUpdate({ values, sid: match.params.sid }), [onUpdate, match.params.sid]);

  return (
    <S.Dialog open={open} onClose={onClose}>
      <S.DialogTitle>
        <S.Heading>
          <FormattedMessage {...messages.groupSettingsDialogDialogTitle} />
        </S.Heading>
      </S.DialogTitle>
      <Formik
        initialValues={{
          updated: {
            clazzGroups: compose(
              reduce((acc, cur) => ({ ...acc, [cur.id]: toString(cur.class_name) }), {}),
              get('clazzGroups'),
            )(groups),
            customGroups: compose(
              reduce((acc, cur) => ({ ...acc, [cur.id]: cur.name }), {}),
              get('customGroups'),
            )(groups),
          },
          removed: { clazzGroups: [], customGroups: [] },
        }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <>
            <Form>
              <S.DialogContent>
                <S.GroupList>
                  {compose(
                    map.convert({ cap: false })((gradeGroup, key) => (
                      <React.Fragment key={`gradeGroup_${key}`}>
                        <S.GroupItem
                          data-type="gradeGroup"
                          data-value={key}
                          onClick={collapse}
                        >
                          <S.ListItemIcon>
                            <S.GroupOutlined />
                          </S.ListItemIcon>
                          <S.ListItemText>{`${key}학년 (${sumBy(g => g.contacts_count, gradeGroup)}명)`}</S.ListItemText>
                          {collapsed === key ? <S.ExpandLess /> : <S.ExpandMore />}
                        </S.GroupItem>
                        <Collapse in={collapsed === key}>
                          <S.SubgroupList>
                            {map(clazz => (
                              <S.SubgroupItem
                                key={clazz.id}
                                data-type="clazzGroup"
                                data-value={clazz.id}
                                disableRipple
                              >
                                <Field name={`updated.clazzGroups[${clazz.id}]`}>
                                  {({ field, form }) => (editing.value === clazz.id && editing.type === 'clazzGroup' ? (
                                    <>
                                      <S.Label htmlFor={`clazzGroup_${clazz.id}`} />
                                      <S.Input type="text" id={`clazzGroup_${clazz.id}`} {...field} />
                                      <S.FlatButton
                                        type="button"
                                        data-name={field.value}
                                        data-value={clazz.id}
                                        onClick={() => {
                                          form.setFieldValue(field.name, prevValue);

                                          setEditing({ value: null, type: null });
                                        }}
                                      >
                                        취소
                                      </S.FlatButton>
                                      <S.FlatButton type="button" onClick={closeEditView}>
                                        확인
                                      </S.FlatButton>
                                    </>
                                  ) : (
                                    <>
                                      <S.ListItemText>{`${field.value}반 (${clazz.contacts_count}명)`}</S.ListItemText>
                                      <S.IconButton
                                        type="button"
                                        size="small"
                                        data-type="clazzGroup"
                                        data-name={field.value}
                                        data-value={clazz.id}
                                        onClick={openEditView}
                                      >
                                        <S.EditOutlined />
                                      </S.IconButton>
                                      <S.IconButton
                                        type="button"
                                        size="small"
                                        onClick={() => {
                                          setGroup({
                                            value: `${clazz.grade}학년 ${clazz.class_name}반`,
                                            type: 'clazzGroup',
                                            id: clazz.id,
                                          });

                                          onDeleteGroupCheckOpen();
                                        }}
                                      >
                                        <S.DeleteOutline />
                                      </S.IconButton>
                                    </>
                                  ))}
                                </Field>
                              </S.SubgroupItem>
                            ), gradeGroup)}
                          </S.SubgroupList>
                        </Collapse>
                      </React.Fragment>
                    )),
                    groupBy(clazzGroup => clazzGroup.grade),
                    reject(clazzGroup => includes(clazzGroup.id, values.removed.clazzGroups)),
                    get('clazzGroups'),
                  )(groups)}
                  {compose(
                    map(customGroup => (
                      <S.GroupItem
                        key={`customGroup_${customGroup.id}`}
                        data-type="customGroup"
                        data-value={customGroup.id}
                        data-set="123"
                        padding={{ right: 21 }}
                        disableRipple
                      >
                        <S.ListItemIcon>
                          <S.GroupOutlined />
                        </S.ListItemIcon>
                        <Field name={`updated.customGroups[${customGroup.id}]`}>
                          {({ field, form }) => (editing.value === customGroup.id && editing.type === 'customGroup'
                            ? (
                              <>
                                <S.Label htmlFor={`customGroup_${customGroup.id}`}>그룹명</S.Label>
                                <S.Input type="text" id={`customGroup_${customGroup.id}`} {...field} />
                                <S.FlatButton
                                  type="button"
                                  data-name={field.value}
                                  data-value={customGroup.id}
                                  onClick={() => {
                                    form.setFieldValue(field.name, prevValue);

                                    setEditing({ value: null, type: null });
                                  }}
                                >
                                  취소
                                </S.FlatButton>
                                <S.FlatButton type="button" onClick={closeEditView}>
                                  확인
                                </S.FlatButton>
                              </>
                            )
                            : (
                              <>
                                <S.ListItemText>{`${field.value} (${customGroup.contacts_count}명)`}</S.ListItemText>
                                <S.IconButton
                                  type="button"
                                  size="small"
                                  data-type="customGroup"
                                  data-name={field.value}
                                  data-value={customGroup.id}
                                  onClick={openEditView}
                                >
                                  <S.EditOutlined />
                                </S.IconButton>
                                <S.IconButton
                                  type="button"
                                  size="small"
                                  onClick={() => {
                                    setGroup({ value: customGroup.name, type: 'customGroup', id: customGroup.id });

                                    onDeleteGroupCheckOpen();
                                  }}
                                >
                                  <S.DeleteOutline />
                                </S.IconButton>
                              </>
                            ))}
                        </Field>
                      </S.GroupItem>
                    )),
                    reject(customGroup => customGroup.is_unspecified_group || includes(customGroup.id, values.removed.customGroups)),
                    get('customGroups'),
                  )(groups)}
                </S.GroupList>
                <S.GroupAddButton type="button" onClick={handleClick}>
                  <S.Add />
                  새 그룹 추가
                </S.GroupAddButton>
              </S.DialogContent>
              <S.DialogActions>
                <Button type="button" variant="outlined" onClick={onClose}>
                  취소
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  적용
                </Button>
              </S.DialogActions>
            </Form>
            <DeleteGroupCheckDialog
              group={group.value}
              open={deleteGroupCheckOpen}
              onClose={onDeleteGroupCheckClose}
              onClick={() => {
                setFieldValue(
                  group.type === 'clazzGroup' ? 'removed.clazzGroups' : 'removed.customGroups',
                  [
                    ...values.removed[group.type === 'clazzGroup'
                      ? 'clazzGroups' : 'customGroups'],
                    group.id,
                  ],
                );

                setFieldValue(
                  group.type === 'clazzGroup' ? 'updated.clazzGroups' : 'updated.customGroups',
                  pickBy(
                    (value, key) => key !== toString(group.id),
                    values.updated[group.type === 'clazzGroup' ? 'clazzGroups' : 'customGroups'],
                  ),
                );
              }}
            />
          </>
        )}
      </Formik>
    </S.Dialog>
  );
};

GroupSettingsDialog.propTypes = {
  groups: PropTypes.shape({}),
  open: PropTypes.bool.isRequired,
  deleteGroupCheckOpen: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGroupAddOpen: PropTypes.func.isRequired,
  onDeleteGroupCheckOpen: PropTypes.func.isRequired,
  onDeleteGroupCheckClose: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

GroupSettingsDialog.defaultProps = {
  groups: null,
};

export default withRouter(GroupSettingsDialog);
